import type { CSSProperties, ReactNode } from 'react';

import type { BlockStyle } from '@/blocks/domain/types';
import { customPartyLabel } from '@/blocks/domain/helpers/roleCategories';
import type { RoleCategory, RoleResponsibilityRow } from '@/blocks/domain/types/BlockContent/RolesResponsibilitiesContent';
import { customerVisibleRows } from '@/common/domain/utils/customerVisibleRows';
import { resolveRowPartyLabel } from '@/document-render/application/resolveRolePartyLabel';
import type { DocumentContext } from '@/document-render/domain/DocumentContext';
import { blockStyleToCss } from '@/document-render/application/blockStyleToCss';
import { resolveTokens } from '@/document-render/application/resolveTokens';
import { keepsUnboundTokens } from '@/document-render/application/unboundTokenHtml';
import { useDocumentAudience } from '@/document-render/presentation/primitives/useDocumentAudience';
import { useDocumentSurface } from '@/document-render/presentation/primitives/useDocumentSurface';
import { resolveDisplayText } from '../fields/unboundTokenDisplay';

const CATEGORY_GAP_PX = 16;

const HEADING_STYLE: CSSProperties = { fontWeight: 700, marginBottom: 6 };

/** One responsibility: its text on the left, the party it belongs to right-aligned on the same line. */
const ROW_STYLE: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 16,
  padding: '4px 0'
};
const ROW_TEXT_STYLE: CSSProperties = { flex: 1, minWidth: 0 };
const ROW_PARTY_STYLE: CSSProperties = { flexShrink: 0, textAlign: 'right', whiteSpace: 'nowrap' };

export function RolesCategoryView({
  categories,
  context,
  title,
  style,
  knownPaths
}: {
  categories: RoleCategory[];
  context: DocumentContext;
  /** Already resolved by the caller. */
  title?: string;
  /** The block's own style, so the category layout is dressed like the flat table (and the PDF). */
  style?: BlockStyle;
  /** Lets a `{{token}}` in a subheading or responsibility resolve exactly like the flat table's cells do. */
  knownPaths?: ReadonlySet<string>;
}) {
  const audience = useDocumentAudience();
  const resolveOptions = knownPaths ? { knownPaths } : undefined;
  // Sample data off (editor): a `{{token}}` in a subheading or responsibility stays a chip.
  const unbound = keepsUnboundTokens(context, useDocumentSurface());
  const display = (text: string) =>
    resolveDisplayText(text, { unbound, resolve: (value) => resolveTokens(value, context, resolveOptions) });
  // A free-text party kept from the legacy form can hold a `{{token}}` too; it follows the same rule as the row text.
  const partyDisplay = (row: RoleResponsibilityRow): ReactNode => {
    const custom = unbound ? customPartyLabel(row) : undefined;
    return custom === undefined ? resolveRowPartyLabel(row, context, resolveOptions) : display(custom);
  };

  return (
    <section aria-label="block-roles-responsibilities" style={style ? blockStyleToCss(style) : undefined}>
      {title ? <div style={HEADING_STYLE}>{title}</div> : null}
      {categories.map((category, index) => {
        const rows = audience === 'customer' ? customerVisibleRows(category.rows) : category.rows;
        // A group the author has not named yet prints no heading, not an empty band above its rows.
        let heading: ReactNode = '';
        if (category.headingVisible !== false) {
          heading = unbound ? display(category.name.trim()) : resolveTokens(category.name, context, resolveOptions).trim();
        }
        return (
          <div key={category.id} style={{ marginTop: index === 0 ? 0 : CATEGORY_GAP_PX }}>
            {heading ? <div style={HEADING_STYLE}>{heading}</div> : null}
            {rows.map((row) => (
              <div key={row.id} style={{ ...ROW_STYLE, opacity: row.hidden ? 0.5 : 1 }}>
                <span style={ROW_TEXT_STYLE}>{display(row.text)}</span>
                <span style={ROW_PARTY_STYLE}>{partyDisplay(row)}</span>
              </div>
            ))}
          </div>
        );
      })}
    </section>
  );
}
